import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl } from '../../../utils/mediaUtils';
import { 
  Images, 
  Upload, 
  Clock, 
  Plus, 
  Trash2, 
  Save,
  Eye,
  X
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...extra
  };
};

const fieldLabels = {
  kicker: 'Section Kicker',
  title: 'Main Title',
  description: 'Section Description'
};

export default function FacilitiesGalleryForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Photo Gallery',
    title: 'Moments Across Our Campus',
    description: 'Explore the vibrant life and learning happening every day across our campus spaces. From classrooms to labs, arts to athletics, discover the heart of our school community.',
    items: [
      { id: 'gallery-classrooms', title: 'Classroom Moments', description: 'Bright learning spaces that support collaboration, focused study, and interactive engagement with modern educational technology.', image: '' },
      { id: 'gallery-labs', title: 'Science & ICT Labs', description: 'Hands-on experiments and digital exploration in modern labs where students discover, innovate, and develop critical thinking skills.', image: '' },
      { id: 'gallery-arts', title: 'Arts & Performance', description: 'Creative rooms for music, art, and student showcases where talent flourishes and self-expression is celebrated.', image: '' },
      { id: 'gallery-sports', title: 'Sports & Activities', description: 'Fields and courts that keep students active, engaged, and developing teamwork, leadership, and athletic excellence.', image: '' }
    ]
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false
  });
  const [pendingItems, setPendingItems] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});

  useEffect(() => {
    const init = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache', ...getAuthHeaders() }
      });
      if (!res.ok) return;
      const json = await res.json();
      if (json.ok && Array.isArray(json.data)) {
        const map = {};
        json.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('[FacilitiesGalleryForm] Media fetch error:', err.message);
    }
  };

  const resolveImage = (mediaRef, index) => {
    if (!mediaRef || (index !== undefined && brokenImages[index])) return '';
    if (typeof mediaRef === 'object' && mediaRef !== null) {
      mediaRef = mediaRef.image || mediaRef.url || mediaRef.file_path || '';
    }
    const str = String(mediaRef).trim();
    if (!str) return '';
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (mediaMap && (mediaMap[str] || mediaMap[String(str)])) {
      const mapped = mediaMap[str] || mediaMap[String(str)];
      if (mapped.startsWith('http://') || mapped.startsWith('https://')) return mapped;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${mapped}` : mapped;
    }
    const resolved = getImageUrl(str, mediaMap);
    if (resolved && resolved !== str) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://')) return resolved;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${resolved}` : resolved;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }
    return '';
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=facilities`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'facilities-gallery' || s.section_type === 'gallery');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=facilities`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'facilities-gallery' || s.section_type === 'gallery');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);

        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const rawItems = Array.isArray(en.items) && en.items.length > 0 ? en.items : formData.items;

        const loadedData = {
          kicker: en.kicker || 'Photo Gallery',
          title: en.title || 'Moments Across Our Campus',
          description: en.description || 'Explore the vibrant life and learning happening every day across our campus spaces.',
          items: rawItems.map((item, idx) => ({
            id: item.id || `gallery-${idx + 1}`,
            title: item.title || '',
            description: item.description || '',
            image: item.image || ''
          }))
        };

        const pFields = { kicker: false, title: false, description: false };
        const pItems = {};

        // Fetch pending changes
        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  let val = c.new_value;
                  if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
                    try { val = JSON.parse(val); } catch (e) {}
                  }

                  if (['kicker', 'title', 'description'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                    if (val !== undefined && val !== null) {
                      loadedData[c.field_id] = val;
                    }
                  } else if (c.field_id?.includes('items.')) {
                    const m = c.field_id.match(/items\.(\d+)(?:\.(add|update|delete))?/);
                    if (m) {
                      const itemIdx = parseInt(m[1], 10);
                      const op = m[2] || 'update';
                      if (op === 'delete') {
                        pItems[`item_${itemIdx}_delete`] = true;
                        pItems[`item_${itemIdx}`] = true;
                      } else {
                        pItems[`item_${itemIdx}`] = true;
                        if (val && typeof val === 'object') {
                          if (loadedData.items[itemIdx]) {
                            loadedData.items[itemIdx] = { ...loadedData.items[itemIdx], ...val };
                          } else {
                            loadedData.items.push(val);
                          }
                        }
                      }
                    }
                  }
                }
              });
            }
          }
        } catch (pe) {
          console.warn('[FacilitiesGalleryForm] Pending check error:', pe);
        }

        setFormData(loadedData);
        setOriginalData(loadedData);
        setPendingFields(pFields);
        setPendingItems(pItems);
      }
    } catch (err) {
      console.error('[FacilitiesGalleryForm] Load error:', err);
      toast.error('Gagal memuat konten Photo Gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...prev.items];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, items: next };
    });
  };

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: `gallery-${Date.now()}`,
          title: '',
          description: '',
          image: ''
        }
      ]
    }));
  };

  const handleRemoveItem = async (index) => {
    if (formData.items.length <= 1) {
      toast.warning('Setidaknya harus ada satu foto galeri');
      return;
    }

    const item = formData.items[index];
    const key = `item_${index}`;
    if (pendingItems[key] || pendingItems[`item_${index}_delete`]) {
      toast.info('Item ini sedang dalam proses review/approval');
      return;
    }

    const origIndex = (originalData.items || []).findIndex(
      orig => (orig.id && item.id && String(orig.id) === String(item.id)) || 
              (orig.title && item.title && orig.title.trim().toLowerCase() === item.title.trim().toLowerCase())
    );
    const originalItem = origIndex !== -1 ? originalData.items[origIndex] : originalData.items?.[index];
    const isExistingInDb = Boolean(originalItem && (originalItem.id || originalItem.title || originalItem.image));

    if (!isExistingInDb) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
      return;
    }

    const confirmDelete = window.confirm(`Ajukan penghapusan foto galeri "${item.title || `Item #${index + 1}`}" untuk persetujuan Kepala Sekolah?`);
    if (!confirmDelete) return;

    const targetIndex = origIndex !== -1 ? origIndex : index;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${targetIndex}.delete`,
          field_name: `Delete Gallery Item: ${item.title || `Item #${targetIndex + 1}`}`,
          old_value: originalItem || item,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Pengajuan penghapusan "${item.title || `Item #${index + 1}`}" berhasil dikirim untuk approval`);
        setPendingItems(prev => ({ ...prev, [key]: true, [`item_${index}_delete`]: true }));
      } else {
        toast.error(result.error || result.message || 'Gagal mengajukan penghapusan');
      }
    } catch (err) {
      console.error('[FacilitiesGalleryForm] Delete error:', err);
      toast.error('Terjadi kesalahan: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleImageUpload = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran file foto maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Hanya format JPG, PNG, dan WebP yang didukung');
      return;
    }

    setImageUploading(prev => ({ ...prev, [index]: true }));
    setUploadProgress(prev => ({ ...prev, [index]: 0 }));

    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('section_type', 'facilities-gallery');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const percent = Math.round((ev.loaded / ev.total) * 100);
        setUploadProgress(prev => ({ ...prev, [index]: percent }));
      }
    };

    xhr.onload = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      setUploadProgress(prev => ({ ...prev, [index]: 0 }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          const mediaId = data.media_id || data.id || res.media_id || res.id;
          const returnedUrl = data.url || data.file_path || data.path || res.url || (data.filename ? `/api/uploads/images/${data.filename}` : '');
          const origin = (apiBase || '').replace(/\/api\/?$/, '');
          const fullUrl = returnedUrl.startsWith('http') ? returnedUrl : (returnedUrl ? `${origin}${returnedUrl}` : '');
          const finalUrl = fullUrl || returnedUrl || String(mediaId || '');

          if (finalUrl) {
            handleItemChange(index, 'image', finalUrl);
            setBrokenImages(prev => ({ ...prev, [index]: false }));
            if (mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: finalUrl,
                [String(mediaId)]: finalUrl
              }));
            }
            toast.success('Foto galeri berhasil diupload');
          } else {
            toast.error('Gagal membaca URL gambar dari server');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      setUploadProgress(prev => ({ ...prev, [index]: 0 }));
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleSubmitField = async (field) => {
    if (!sectionId) {
      toast.error('Section ID belum dimuat');
      return;
    }

    if (formData[field] === originalData[field]) {
      toast.warning('Tidak ada perubahan pada kolom ini');
      return;
    }

    setSubmitting(prev => ({ ...prev, [field]: true }));
    const label = fieldLabels[field] || (field.charAt(0).toUpperCase() + field.slice(1));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: label,
          old_value: originalData[field] || '',
          new_value: formData[field],
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${label}" berhasil dikirim untuk approval`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: formData[field] }));
      } else {
        toast.error(result.error || result.message || 'Gagal mengirim pengajuan');
      }
    } catch (err) {
      console.error('[FacilitiesGalleryForm] Submit error:', err);
      toast.error('Error saat submit: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmitItem = async (index) => {
    if (!sectionId) {
      toast.error('Section ID belum dimuat');
      return;
    }

    const item = formData.items[index];
    if (!item?.title) {
      toast.warning('Silakan isi judul foto galeri');
      return;
    }
    if (!item?.image || brokenImages[index]) {
      toast.warning('Silakan upload foto terlebih dahulu sebelum submit');
      return;
    }

    const isNew = !originalData.items?.[index] || !originalData.items[index]?.id;
    const op = isNew ? 'add' : 'update';
    const key = `item_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.${op}`,
          field_name: isNew ? `Add Gallery Item: ${item.title}` : `Gallery Item: ${item.title}`,
          old_value: isNew ? null : (originalData.items?.[index] || {}),
          new_value: item,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(isNew 
          ? `Pengajuan tambah foto "${item.title}" berhasil disubmit untuk approval`
          : `Foto "${item.title}" berhasil disubmit untuk approval`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          next[index] = item;
          return { ...prev, items: next };
        });
      } else {
        toast.error(result.error || result.message || 'Gagal mengirim pengajuan');
      }
    } catch (err) {
      console.error('[FacilitiesGalleryForm] Item submit error:', err);
      toast.error('Error saat submit item: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Memuat bagian Photo Gallery...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img 
              src={lightboxImage} 
              alt="Preview" 
              style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: '12px', objectFit: 'contain' }}
            />
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
          </div>
        </div>
      )}

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Images size={22} />
        </div>
        <div>
          <h2 className="unified-title">Photo Gallery</h2>
          <p className="unified-subtitle">
            Kelola judul bagian, pengantar, dan album foto momen pembelajaran di kampus Permata Harapan
          </p>
        </div>
      </div>

      {/* Field: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker || submitting.kicker}
            placeholder="e.g. Photo Gallery"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Menyimpan...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title || submitting.title}
            placeholder="e.g. Moments Across Our Campus"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Menyimpan...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={pendingFields.description || submitting.description}
            placeholder="Deskripsi galeri momen kampus..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('description')}
            disabled={pendingFields.description || submitting.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Menyimpan...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Gallery Items List */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Gallery Photographs ({formData.items.length})</label>
            <p className="unified-field-desc">
              Daftar foto panggung teater galeri kampus dengan foto, judul, dan deskripsi momen
            </p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleAddItem}
          >
            <Plus size={14} /> Add Photo
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: '20px', marginTop: '16px' }}>
          {formData.items.map((item, idx) => {
            const isPending = pendingItems[`item_${idx}`] || pendingItems[`item_${idx}_delete`];
            const isSubmitting = submitting[`item_${idx}`];
            const isUploading = imageUploading[idx];
            const previewUrl = resolveImage(item.image, idx);

            const origItem = originalData.items?.[idx];
            const isNewItem = !origItem || !origItem.id;
            const hasTitleChanged = (item.title || '') !== (origItem?.title || '');
            const hasDescChanged = (item.description || '') !== (origItem?.description || '');
            const hasImgChanged = String(item.image || '') !== String(origItem?.image || '');
            const hasChanged = isNewItem || hasTitleChanged || hasDescChanged || hasImgChanged;

            const isSubmitDisabled = isPending || isSubmitting || !item.title || !item.image || brokenImages[idx] || !hasChanged;

            return (
              <div 
                key={item.id || idx} 
                style={{ 
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0', 
                  borderRadius: '14px', 
                  background: isPending ? '#fffdfa' : '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Photo Header / Dropzone */}
                <div 
                  style={{ 
                    height: '160px', 
                    background: '#0f172a',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {previewUrl ? (
                    <>
                      <img 
                        src={previewUrl} 
                        alt={item.title || `Photo ${idx + 1}`} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => setLightboxImage(previewUrl)}
                        onError={() => setBrokenImages(prev => ({ ...prev, [idx]: true }))}
                      />
                      <button
                        type="button"
                        onClick={() => setLightboxImage(previewUrl)}
                        style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.65)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Lihat Foto"
                      >
                        <Eye size={13} />
                      </button>
                    </>
                  ) : (
                    <label 
                      style={{
                        width: '100%',
                        height: '100%',
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isPending || isUploading ? 'not-allowed' : 'pointer',
                        gap: '6px',
                        padding: '16px',
                        textAlign: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ background: '#e2e8f0', borderRadius: '50%', padding: '10px', display: 'flex' }}>
                        <Upload size={20} color="#64748b" />
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                        {isUploading ? `Mengupload (${uploadProgress[idx] || 0}%)...` : 'Upload Foto Galeri'}
                      </span>
                      {isUploading && (
                        <div style={{ width: '80%', height: '4px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden', marginTop: '4px' }}>
                          <div style={{ width: `${uploadProgress[idx] || 0}%`, height: '100%', background: '#7c3aed', transition: 'width 0.2s ease' }} />
                        </div>
                      )}
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>JPG, PNG, WebP (Maks 5MB)</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => handleImageUpload(idx, e)}
                        disabled={isPending || isUploading}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>

                {/* Card Body & Form Inputs */}
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, justifyContent: 'space-between' }}>
                  <div>
                    {/* Item Top Bar */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>
                          #{idx + 1} {item.title || 'Foto Baru'}
                        </span>
                        {isPending && (
                          <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                            <Clock size={11} /> {pendingItems[`item_${idx}_delete`] ? 'Pending Delete' : 'Pending Review'}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(idx)}
                        disabled={isPending || formData.items.length <= 1}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: isPending || formData.items.length <= 1 ? '#cbd5e1' : '#ef4444', 
                          cursor: isPending || formData.items.length <= 1 ? 'not-allowed' : 'pointer', 
                          padding: '4px' 
                        }}
                        title="Hapus Foto"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    {/* Title */}
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                        Photo Title *
                      </label>
                      <input
                        type="text"
                        className="unified-input"
                        value={item.title || ''}
                        onChange={(e) => handleItemChange(idx, 'title', e.target.value)}
                        disabled={isPending}
                        placeholder="e.g. Science Lab Experiments"
                        style={{ width: '100%' }}
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                        Description
                      </label>
                      <textarea
                        className="unified-textarea"
                        rows={3}
                        value={item.description || ''}
                        onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                        disabled={isPending}
                        placeholder="Penjelasan momen dan aktivitas pada foto ini..."
                        style={{ width: '100%', resize: 'vertical' }}
                      />
                    </div>
                  </div>

                  {/* Card Actions: Change Photo & Save & Submit */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                    {previewUrl && (
                      <label 
                        className="unified-secondary-btn"
                        style={{ 
                          cursor: isPending || isUploading ? 'not-allowed' : 'pointer',
                          justifyContent: 'center',
                          fontSize: '12px',
                          padding: '7px 12px'
                        }}
                      >
                        <Upload size={13} />
                        {isUploading ? `Mengupload (${uploadProgress[idx] || 0}%)...` : 'Ganti Foto'}
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={(e) => handleImageUpload(idx, e)}
                          disabled={isPending || isUploading}
                          style={{ display: 'none' }}
                        />
                      </label>
                    )}

                    <button
                      type="button"
                      className="unified-submit-button"
                      style={{ width: '100%', justifyContent: 'center', padding: '8px 14px', fontSize: '13px' }}
                      onClick={() => handleSubmitItem(idx)}
                      disabled={isSubmitDisabled}
                    >
                      <Save size={14} /> {isSubmitting ? 'Menyimpan...' : 'Save & Submit Photo'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
